/**
 * MetricGrid - Multiple KPIs in a responsive grid
 *
 * Usage:
 *   <MetricGrid data={{
 *     metrics: [
 *       { value: 8241, label: 'Steps', trend: 12, icon: '👟' },
 *       { value: 6.8, label: 'Sleep', unit: 'hrs', icon: '😴' },
 *       { value: 142, label: 'Resting HR', unit: 'bpm', icon: '❤️' },
 *     ]
 *   }} />
 */

import type { BlockProps, MetricGridData } from './types';
import { StatCard } from './StatCard';
import './blocks.css';

export interface MetricGridProps extends BlockProps<MetricGridData> {
  /** Click handler for individual metrics */
  onMetricClick?: (index: number) => void;
}

export function MetricGrid({ data, title, config, onMetricClick, className }: MetricGridProps) {
  const { metrics, columns } = data;

  if (!metrics || metrics.length === 0) {
    return (
      <div className={`metric-grid ${className || ''}`}>
        {title && <div className="metric-grid-title">{title}</div>}
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No metrics to display
        </div>
      </div>
    );
  }

  const gridColumns = columns || Math.min(metrics.length, 4);

  return (
    <div className={`metric-grid ${className || ''}`}>
      {title && <div className="metric-grid-title">{title}</div>}

      <div
        className="metric-grid-container"
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
        }}
      >
        {metrics.map((metric, index) => (
          <StatCard
            key={index}
            data={metric}
            onClick={onMetricClick ? () => onMetricClick(index) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default MetricGrid;
